class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartUpdateUnsubscriber = undefined;
    this.overlay = this.querySelector('.drawer-overlay');
    // Make overlay clickable to close drawer (fallback for iOS 26)
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer') {
        return;
      }
      // When cart page updates, fetch fresh drawer content
      if (event.source === 'cart-items') {
        fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            // Update cart-drawer-items
            const sourceItems = html.querySelector('cart-drawer-items');
            const targetItems = this.querySelector('cart-drawer-items');
            if (sourceItems && targetItems) {
              targetItems.innerHTML = sourceItems.innerHTML;
            }

            // Update drawer footer
            const sourceFooter = html.querySelector('.drawer__footer');
            const targetFooter = this.querySelector('.drawer__footer');
            if (sourceFooter && targetFooter) {
              targetFooter.innerHTML = sourceFooter.innerHTML;
            }
          })
          .catch((e) => {
            console.error('Error updating cart drawer:', e);
          });
      } else {
        this.renderContents(event.cartData);
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Insert overlay before drawer in DOM to ensure proper stacking
    const drawerElement = document.querySelector('cart-drawer');
    if (drawerElement && drawerElement.parentNode) {
      drawerElement.parentNode.insertBefore(this.overlay, drawerElement);
    } else {
      document.body.appendChild(this.overlay);
    }
    this.overlay.classList.remove('hidden');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);

    // Lock body scroll - prevent background scrolling when drawer is open
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    this.scrollY = scrollY; // Store scroll position for restore

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    // Unlock body scroll - restore scroll position
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.classList.remove('modal-open');

    // Restore scroll position
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
      this.scrollY = undefined;
    }

    this.overlay.classList.add('hidden');
    this.appendChild(this.overlay);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'),
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      // Skip if section element doesn't exist on page OR section HTML not in response
      if (!sectionElement || !parsedState.sections[section.id]) return;

      const inner = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);

      if (inner) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector,
        );
      }
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    // setTimeout(() => {
    //   this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    //   // Don't call this.open() here as it creates duplicate instances
    //   // The drawer should already be open when updating contents
    // });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'main-cart-footer',
        selector: '.js-contents',
      },
      {
        id: 'main-cart-items',
        selector: '.cart-contents',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Wait for CartItems to be defined before creating CartDrawerItems
function defineCartDrawerItems() {
  if (typeof CartItems === 'undefined') {
    // CartItems not loaded yet, wait a bit and try again
    setTimeout(defineCartDrawerItems, 10);
    return;
  }

  // customEvent function is now defined in cart.js and available globally

  class CartDrawerItems extends CartItems {
    connectedCallback() {
      super.connectedCallback();
      // Disable HTML5 validation on all quantity inputs
      this.disableQuantityValidation();

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.cart-upsell button');
        const closeBtn = event.target.closest('.cart-upsell-close');

        const upsell =
          button?.closest('.cart-upsell') ||
          closeBtn?.closest('.cart-upsell') ||
          closeBtn?.closest('.upsell-success-wrapper');

        if (closeBtn) {
          event.preventDefault();
          upsell.classList.add('hidden');
          if (upsell.classList.contains('cart-upsell')) {
            customEvent('cart_upsell', 'cart_upsell_close');
          }
          return;
        }

        if (button) {
          event.preventDefault();

          button.classList.add('disabled');
          button.setAttribute('aria-disabled', 'true');

          button.style.pointerEvents = 'none';

          if (!upsell) return;

          this.upgradeVariant(
            upsell.dataset.index,
            upsell.dataset.key,
            upsell.dataset.variant,
            upsell.dataset.quantity,
            upsell.dataset.upgrade,
            upsell.dataset.price_difference,
          );
        }
      });
    }

    // upgradeVariant method is inherited from CartItems class
    // No need to override - the parent class handles both drawer and cart page contexts

    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: 'cart-drawer-items',
        },
        {
          id: 'cart-drawer-footer',
          section: 'cart-drawer',
          selector: '.drawer__footer',
        },
      ];

      // Only include cart page sections if they exist on the current page
      const mainCartFooter = document.getElementById('main-cart-footer');
      const mainCartItems = document.getElementById('main-cart-items');

      if (mainCartFooter) {
        sections.push({
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id || 'main-cart-footer',
          selector: '.js-contents',
        });
      }

      if (mainCartItems) {
        sections.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id || 'main-cart-items',
          selector: '.cart-contents',
        });
      }

      return sections;
    }

    updateQuantity(line, quantity, event, name, variantId) {
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (parsedState.errors) {
            const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedState.errors);
            this.disableLoading(line);
            return;
          }

          // Update all sections that exist on the page
          if (parsedState.sections) {
            this.getSectionsToRender().forEach((section) => {
              if (!parsedState.sections[section.section]) return;

              // Find the element to update
              let elementToReplace;
              if (section.id === 'cart-drawer' || section.id === 'cart-drawer-footer') {
                // For cart drawer sections, find by selector directly
                elementToReplace = document.querySelector(section.selector);

                // Skip if drawer element doesn't exist
                if (!elementToReplace) {
                  return;
                }

                // Extract the content from the section HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = parsedState.sections[section.section];
                const sourceElement = tempDiv.querySelector(section.selector);

                if (sourceElement) {
                  elementToReplace.innerHTML = sourceElement.innerHTML;
                }
              } else {
                // For other sections, find by ID first
                const sectionElement = document.getElementById(section.id);
                if (!sectionElement) {
                  return;
                }
                elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;

                if (elementToReplace) {
                  elementToReplace.innerHTML = this.getSectionInnerHTML(
                    parsedState.sections[section.section],
                    section.selector,
                  );
                }
              }
            });
          }

          this.disableLoading(line);

          // Re-disable quantity validation after DOM update
          this.disableQuantityValidation();

          // Publish cart update event for other components
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer',
            cartData: parsedState,
            variantId: variantId,
          });
        })
        .catch((error) => {
          console.error(error);
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) {
            errors.textContent = window.cartStrings.error;
          }
          this.disableLoading(line);
        });
    }

    disableQuantityValidation() {
      const quantityInputs = document.querySelectorAll('input[type="number"][name="updates[]"]');
      quantityInputs.forEach((input) => {
        input.removeAttribute('max');
        input.setCustomValidity('');
        input.addEventListener('invalid', (e) => {
          e.preventDefault();
          e.target.setCustomValidity('');
        });
      });
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

// Start the definition process
defineCartDrawerItems();
